import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  chipClasses,
  svgIconClasses,
} from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  GetApp as DownloadIcon,
  MoreVert as MoreVertIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import {
  grayColor,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react.js";
import AuthContext from "connections/auth/AuthProvider.js";
import {
  deleteVariant,
  downloadVariantSourceFile,
  renameVariant,
} from "views/Inputs/viewServices/apiCalls";
import TagInput from "./TagInput";

const useStyles = makeStyles((theme) => ({
  card: {
    border: `1px solid ${grayColor[5]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.25, 2, 2),
    width: 200,
    minWidth: 180,
    minHeight: 144,
    maxWidth: 200,
    display: "flex",
    flexDirection: "column",
    backgroundColor: whiteColor,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      borderColor: grayColor[4],
    },
  },
  variantName: {
    fontWeight: 500,
    fontSize: "0.9rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    lineHeight: 1.25,
    minHeight: "2.25rem",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.25),
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: theme.spacing(0.5),
  },
  actionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.25),
    marginBottom: theme.spacing(0.75),
  },
  actionButton: {
    padding: 2,
    color: grayColor[4],
    [`& .MuiSvgIcon-root, & .${svgIconClasses.root}`]: {
      fontSize: "0.95rem",
    },
    "&:hover": {
      color: (props) => props.clientColor,
    },
  },
  deleteActionButton: {
    padding: 2,
    color: grayColor[4],
    [`& .MuiSvgIcon-root, & .${svgIconClasses.root}`]: {
      fontSize: "0.95rem",
    },
    "&:hover": {
      color: "#d32f2f",
    },
  },
  menuItem: {
    minWidth: 180,
    fontSize: "0.85rem",
    color: grayColor[2],
  },
  menuItemIcon: {
    fontSize: "1rem",
    marginRight: theme.spacing(1),
    color: "inherit",
  },
  deleteMenuItem: {
    color: "#d32f2f",
  },
  tagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    alignItems: "center",
    marginTop: theme.spacing(1),
    overflow: "hidden",
  },
  tagChip: (props) => ({
    height: 22,
    fontSize: "0.7rem",
    backgroundColor: `${props.clientColor}15`,
    border: `1px solid ${props.clientColor}40`,
    color: props.clientColor,
    [`& .${chipClasses.deleteIcon}`]: {
      fontSize: "0.8rem",
      color: props.clientColor,
      "&:hover": {
        color: "#d32f2f",
      },
    },
  }),
  addButton: {
    height: 22,
    width: 22,
    minWidth: 22,
    border: `1px dashed ${grayColor[6]}`,
    borderRadius: "50%",
    padding: 0,
    "&:hover": {
      backgroundColor: grayColor[12],
    },
  },
  addIcon: {
    fontSize: "0.9rem",
    color: grayColor[4],
  },
  noTags: {
    color: grayColor[4],
    fontSize: "0.7rem",
    fontStyle: "italic",
  },
  dialogActions: {
    padding: theme.spacing(1, 3, 2),
  },
  deleteDialogTitle: {
    padding: theme.spacing(2, 3, 0.5),
    color: grayColor[2],
    "& h2": {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.35,
    },
  },
  deleteDialogContent: {
    padding: theme.spacing(1.5, 3, 1),
  },
  deleteWarning: {
    display: "block",
    marginTop: theme.spacing(1),
    lineHeight: 1.4,
  },
  loadingContent: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  buttonSpinner: {
    color: "inherit",
  },
  saveButton: {
    backgroundColor: (props) => props.clientColor || "#1976d2",
    color: "#fff",
    "&:hover": {
      backgroundColor: (props) => props.clientColor || "#1565c0",
    },
    "&:disabled": {
      color: grayColor[6],
    },
  },
  deleteButton: {
    backgroundColor: "#d32f2f",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#b71c1c",
    },
    "&:disabled": {
      color: grayColor[6],
    },
  },
}));

const VariantTagCard = ({
  variant,
  tableId,
  availableTags,
  onAddTag,
  onRemoveTag,
  onVariantChange,
}) => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#1976d2";
  const classes = useStyles({ clientColor });
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });
  const [showInput, setShowInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [newVariantName, setNewVariantName] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const currentTags = variant.tags || [];

  const handleAddTag = (tagName) => {
    // Handle "Create: xyz" format from autocomplete
    const createPrefix = t("create_tag", { name: "" }).replace('""', "");
    if (tagName.startsWith(createPrefix)) {
      tagName = tagName.replace(createPrefix, "").replace(/"/g, "").trim();
    }

    if (tagName && !currentTags.includes(tagName)) {
      onAddTag(variant.variant_id, tagName);
    }
    setShowInput(false);
  };

  const handleRemoveTag = (tagToRemove) => {
    onRemoveTag(variant.variant_id, tagToRemove);
  };

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      await downloadVariantSourceFile(tableId, variant.variant_id);
      toast.success(t("download_started"));
    } catch (error) {
      toast.error(error.response?.data?.error || t("download_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRename = () => {
    setNewVariantName(variant.variant_name || "");
    setIsRenameOpen(true);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuDownload = () => {
    handleCloseMenu();
    handleDownload();
  };

  const handleMenuRename = () => {
    handleCloseMenu();
    handleOpenRename();
  };

  const handleMenuDelete = () => {
    handleCloseMenu();
    setIsDeleteOpen(true);
  };

  const handleRename = async () => {
    const trimmedName = newVariantName.trim();
    if (!trimmedName) {
      toast.error(t("variant_name_required"));
      return;
    }

    setIsLoading(true);
    try {
      await renameVariant(tableId, variant.variant_id, trimmedName);
      toast.success(t("variant_renamed"));
      setIsRenameOpen(false);
      onVariantChange?.();
    } catch (error) {
      toast.error(error.response?.data?.error || t("rename_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await deleteVariant(tableId, variant.variant_id);
      toast.success(t("variant_deleted"));
      setIsDeleteOpen(false);
      onVariantChange?.();
    } catch (error) {
      toast.error(error.response?.data?.error || t("delete_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box className={classes.card}>
        <Box className={classes.cardHeader}>
          <Box className={classes.actionsContainer}>
            <IconButton
              className={classes.actionButton}
              size="small"
              onClick={(event) => setMenuAnchorEl(event.currentTarget)}
              disabled={isLoading}
              title={t("variant_actions", { defaultValue: "Variant actions" })}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Box>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem
            className={classes.menuItem}
            onClick={handleMenuDownload}
            disabled={isLoading || !variant.source_file_id}
          >
            <DownloadIcon className={classes.menuItemIcon} />
            {t("download_source_file")}
          </MenuItem>
          <MenuItem
            className={classes.menuItem}
            onClick={handleMenuRename}
            disabled={isLoading}
          >
            <EditIcon className={classes.menuItemIcon} />
            {t("rename_variant")}
          </MenuItem>
          <MenuItem
            className={`${classes.menuItem} ${classes.deleteMenuItem}`}
            onClick={handleMenuDelete}
            disabled={isLoading}
          >
            <DeleteIcon className={classes.menuItemIcon} />
            {t("delete_variant")}
          </MenuItem>
        </Menu>

        <Typography
          className={classes.variantName}
          title={variant.variant_name}
        >
          {variant.variant_name}
        </Typography>

        <Box className={classes.tagsContainer}>
          {currentTags.length === 0 && !showInput && (
            <Typography className={classes.noTags}>{t("no_tags")}</Typography>
          )}

          {currentTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              className={classes.tagChip}
              onDelete={() => handleRemoveTag(tag)}
            />
          ))}

          {showInput ? (
            <TagInput
              availableTags={availableTags}
              currentTags={currentTags}
              onSelect={handleAddTag}
              onClose={() => setShowInput(false)}
            />
          ) : (
            <IconButton
              className={classes.addButton}
              size="small"
              onClick={() => setShowInput(true)}
              title={t("add_tag")}
            >
              <AddIcon className={classes.addIcon} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Dialog
        open={isRenameOpen}
        onClose={() => setIsRenameOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("rename_variant")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t("variant_name")}
            value={newVariantName}
            onChange={(event) => setNewVariantName(event.target.value)}
            disabled={isLoading}
            margin="dense"
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setIsRenameOpen(false)} disabled={isLoading}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleRename}
            disabled={isLoading}
            variant="contained"
            className={classes.saveButton}
          >
            {t("save_changes")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteOpen}
        onClose={() => {
          if (!isLoading) {
            setIsDeleteOpen(false);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className={classes.deleteDialogTitle}>
          {t("delete_variant_confirm_title")}
        </DialogTitle>
        <DialogContent className={classes.deleteDialogContent}>
          <Typography>
            {t("delete_variant_confirm_message", {
              name: variant.variant_name,
            })}
          </Typography>
          <Typography
            variant="caption"
            color="error"
            className={classes.deleteWarning}
          >
            {t("delete_variant_warning")}
          </Typography>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setIsDeleteOpen(false)} disabled={isLoading}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleDelete}
            disabled={isLoading}
            variant="contained"
            className={classes.deleteButton}
          >
            {isLoading ? (
              <span className={classes.loadingContent}>
                <CircularProgress size={16} className={classes.buttonSpinner} />
                {t("delete_variant")}
              </span>
            ) : (
              t("delete_variant")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

VariantTagCard.propTypes = {
  variant: PropTypes.shape({
    variant_id: PropTypes.number.isRequired,
    variant_name: PropTypes.string.isRequired,
    tags: PropTypes.array,
    source_file_id: PropTypes.number,
  }).isRequired,
  tableId: PropTypes.string.isRequired,
  availableTags: PropTypes.array.isRequired,
  onAddTag: PropTypes.func.isRequired,
  onRemoveTag: PropTypes.func.isRequired,
  onVariantChange: PropTypes.func,
};

export default VariantTagCard;
